import React, { useState, useEffect, useRef } from "react";
import ChatInput from "../components/kcgbetsy/ChatInput";
import { Typewriter } from "react-simple-typewriter";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import { Snackbar, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../styles/Betsy.css";
import TypewriterMarkdown from "../components/kcgbetsy/typewriter";
import ReactMarkdown from "react-markdown";
import { v4 as uuidv4 } from "uuid";
import KcgHeader from "../components/kcgbetsy/kcgheader/kcgheader";
import KcgQuestion from "../components/kcgbetsy/kcgBottomSheet/kcgQuestion";
import Footer from "../components/Footer";
import SpeechControls from "../components/SpeechControls";
import Sidebar from "./Sidebar";
import BetsyFooter from "../components/BetsyFooter";
import config from "../config/config.json";
import { validateToken } from "./ValidateToken";
import OnScreenMessages from "../OnScreenMessages/messages.json";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const betsyLogo = "/img/kcgbetsy/Betsy.png";
const userLogo = "/img/kcgbetsy/avatar.avif";

var WelcomeMSg = `Hello there! I'm BETSY — your <u>B</u>ODEX <u>E</u>nhanced <u>T</u>rusted <u>S</u>idekick for <u>Y</u>ou.

You can ask me anything related to Utah Informed 2025 - A Report created by Kem C. Gardner Institute.`;

const KCG = ({ initialQuery }) => {
  let customIndex = 0;
  const scrollRef = useRef();
  const [messages, setMessages] = useState([]);
  const [sessionid, setSessionId] = useState("");
  const messagesEndRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [snackBarMsg, setSnackbarMsg] = useState(false);
  const [regeneratingMsgId, setRegeneratingMsgId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [input, setInput] = useState("");
  const [validToken, setValidToken] = useState(null);

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem("token");
      console.log("Token from localStorage:", token);

      if (!token) {
        setValidToken(false);
        return;
      }

      const { valid, user } = await validateToken(token);
      console.log("Validation Response:", { valid, user });

      if (!valid) {
        setValidToken(false);
        return;
      }

      setValidToken(true);
    };

    checkToken();
  }, []);

  useEffect(() => {
    if (validToken !== null) {
      console.log("✅ Is token valid?", validToken);
    }
  }, [validToken]);

  useEffect(() => {
    const existingSid = localStorage.getItem("kcg_chat_session");
    const savedMessages = localStorage.getItem("kcg_chat_messages");

    if (existingSid) {
      setSessionId(existingSid);
    } else {
      const sid = uuidv4();
      localStorage.setItem("kcg_chat_session", sid);
      setSessionId(sid);
    }

    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages).map((m) => ({
          ...m,
          isAnimated: false,
        }));
        setMessages(parsedMessages);
      } catch (e) {
        console.error("Failed to parse saved chat messages:", e);
        // fallback if corrupted
        setMessages([
          {
            id: uuidv4(),
            sender: "bot",
            text: WelcomeMSg,
            isAnimated: true,
          },
        ]);
      }
    } else {
      // No previous session — start fresh
      setMessages([
        {
          id: uuidv4(),
          sender: "bot",
          text: WelcomeMSg,
          isAnimated: true,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("kcg_chat_messages", JSON.stringify(messages));
    }
  }, [messages]);

  const startNewSession = () => {
    const sid = uuidv4();
    localStorage.setItem("kcg_chat_session", sid);
    localStorage.removeItem("kcg_chat_messages");
    setSessionId(sid);
    setMessages([
      {
        id: uuidv4(),
        sender: "bot",
        text: WelcomeMSg,
        isAnimated: true,
      },
    ]);
  };

  const sendFeedback = async (type, msg_id) => {
    try {
      const res = await fetch(config.API_URL + "/pdf_chatbot/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionid,
          message_index: msg_id,
          feedback: type,
          user: validToken
            ? JSON.parse(localStorage.getItem("userDetails"))?.email
            : "Guest User",
        }),
      });

      if (!res.ok) {
        console.error("Failed to send feedback:", await res.text());
        return;
      }
    } catch (err) {
      console.error("Error sending feedback:", err);
    }
  };

  const handleLike = (msg_id) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === msg_id
          ? { ...msg, feedback: msg.feedback === "like" ? "neutral" : "like" }
          : msg,
      ),
    );
    sendFeedback("like", msg_id);
  };

  const handleDislike = (msg_id) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === msg_id
          ? {
              ...msg,
              feedback: msg.feedback === "dislike" ? "neutral" : "dislike",
            }
          : msg,
      ),
    );
    sendFeedback("dislike", msg_id);
  };

  useEffect(() => {
    if (initialQuery) {
      handleSend(initialQuery);
      initialQuery = "";
    }
  }, [initialQuery]);

  const handleSend = async (msg, feedback) => {
    const feedbackType = feedback ? feedback : "neutral";
    const userMsg = { id: uuidv4(), sender: "user", text: msg };
    const typingId = uuidv4();

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        id: typingId,
        sender: "bot",
        text: "Typing...",
        isTyping: true,
      },
    ]);
    setIsTyping(true); // typing begins

    try {
      const res = await fetch(config.API_URL + "/pdf_chatbot/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: msg,
          filename: "UI2025.pdf",
          file_hash: "17eb95e2ff4c8555d5245c409e21741a",
          sessionId: sessionid,
          feedback: feedbackType,
          user: validToken
            ? JSON.parse(localStorage.getItem("userDetails"))?.email
            : "Guest User",
        }),
      });

      // 🟠 Check for 504 Gateway Timeout
      if (res.status === 504) {
        throw new Error("The AI took too long to respond. Please try again.");
      }

      // 🔴 Any other error (network, server crash, etc.)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      const responseText = data.summary;

      // Estimate animation time: 50ms per character (adjust based on your speed)
      const estimatedTypingDuration = responseText.length * 15;

      setMessages((prev) =>
        prev
          .filter((m) => m.id !== typingId)
          .concat({
            id: data.message_id,
            sender: "bot",
            text: responseText,
            time: data.res_time,
            originalQuestion: msg,
            isAnimated: true,
          }),
      );

      // Wait for typewriter animation to finish
      setTimeout(() => {
        setIsTyping(false);
      }, estimatedTypingDuration + 0); // +300ms as buffer
    } catch (err) {
      console.log(err);

      let gracefulMsg = OnScreenMessages["BTC-2041"].message;

      if (err.message.includes("Unexpected token '<'")) {
        gracefulMsg = OnScreenMessages["BTC-2031"].message;
      } else if (
        err.message.includes("504") ||
        err.message.includes("timeout")
      ) {
        gracefulMsg = OnScreenMessages["BTC-2011"].message;
      } else if (err.message.includes("401")) {
        gracefulMsg = OnScreenMessages["BTC-2061"].message;
      } else if (err.message.includes("429")) {
        gracefulMsg = OnScreenMessages["BTC-2081"].message;
      } else if (err.message.includes("Failed to fetch")) {
        if (!window.navigator.onLine) {
          gracefulMsg = OnScreenMessages["BTC-2071"].message;
        } else {
          gracefulMsg = OnScreenMessages["BTC-2021"].message;
        }
      } else if (err.message.includes("502")) {
        gracefulMsg = OnScreenMessages["BTC-2051"].message;
      } else {
        gracefulMsg = OnScreenMessages["BTC-2091"].message;
      }

      setMessages((prev) =>
        prev
          .filter((m) => m.id !== typingId)
          .concat({
            id: uuidv4(),
            sender: "bot",
            text: gracefulMsg,
            originalQuestion: msg,
          }),
      );
      setIsTyping(false);
    }
  };

  useEffect(() => {
    if (messages.length > 2) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages]);

  const copyText = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySnackbarOpen(true);
      setSnackbarMsg("Copied to Clipboard!");
    } catch {
      setCopySnackbarOpen(true);
      setSnackbarMsg("Failed to copy!");
    }
  };

  const regenerateResponse = async (msg) => {
    setRegeneratingMsgId(msg.id);
    setIsTyping(true); // ✅ Disable send button

    const originalQuestion = msg.originalQuestion;
    const targetMsgId = msg.id;
    const feedback = msg.feedback ? msg.feedback : "neutral";

    try {
      const res = await fetch(config.API_URL + "/pdf_chatbot/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: originalQuestion,
          filename: "UI2025.pdf",
          file_hash: "17eb95e2ff4c8555d5245c409e21741a",
          sessionId: sessionid,
          feedback: feedback,
          user: validToken
            ? JSON.parse(localStorage.getItem("userDetails"))?.email
            : "Guest User",
        }),
      });

      // 🟠 Check for 504 Gateway Timeout
      if (res.status === 504) {
        throw new Error("The AI took too long to respond. Please try again.");
      }

      // 🔴 Any other error (network, server crash, etc.)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      const responseText = data.summary;

      // Estimate animation time: 50ms per character (adjust based on your speed)
      const estimatedTypingDuration = responseText.length * 15;

      setMessages((prevMessages) => {
        return prevMessages.map((m) =>
          m.id === targetMsgId
            ? {
                ...m,
                text: data.summary,
                time: data.res_time,
                id: data.message_id,
                originalQuestion: originalQuestion,
                feedback: "neutral",
                isAnimated: true,
              }
            : m,
        );
      });
      // Wait for typewriter animation to finish
      setTimeout(() => {
        setIsTyping(false);
      }, estimatedTypingDuration + 0); // +300ms as buffer
    } catch (err) {
      console.log(err);

      let gracefulMsg = OnScreenMessages["BTC-2041"].message;

      if (err.message.includes("Unexpected token '<'")) {
        gracefulMsg = OnScreenMessages["BTC-2031"].message;
      } else if (
        err.message.includes("504") ||
        err.message.includes("timeout")
      ) {
        gracefulMsg = OnScreenMessages["BTC-2011"].message;
      } else if (err.message.includes("401")) {
        gracefulMsg = OnScreenMessages["BTC-2061"].message;
      } else if (err.message.includes("429")) {
        gracefulMsg = OnScreenMessages["BTC-2081"].message;
      } else if (err.message.includes("Failed to fetch")) {
        if (!window.navigator.onLine) {
          gracefulMsg = OnScreenMessages["BTC-2071"].message;
        } else {
          gracefulMsg = OnScreenMessages["BTC-2021"].message;
        }
      } else if (err.message.includes("502")) {
        gracefulMsg = OnScreenMessages["BTC-2051"].message;
      } else {
        gracefulMsg = OnScreenMessages["BTC-2091"].message;
      }

      setMessages((prevMessages) =>
        prevMessages.map((m, i) =>
          i === targetMsgId
            ? {
                ...m,
                id: uuidv4(),
                sender: "bot",
                text: gracefulMsg,
                originalQuestion: originalQuestion,
              }
            : m,
        ),
      );
      setIsTyping(false); // ✅ Re-enable if there's an error
    }
    setRegeneratingMsgId(null);
  };

  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  const checkIfScrolledToBottom = () => {
    const chat = chatHistoryRef.current;
    if (!chat) return;
    const isAtBottom =
      chat.scrollTop + chat.clientHeight >= chat.scrollHeight - 20;
    setShowScrollDown(!isAtBottom);
  };

  useEffect(() => {
    const chat = chatHistoryRef.current;
    if (!chat) return;

    const handleScroll = () => checkIfScrolledToBottom();

    chat.addEventListener("scroll", handleScroll);
    return () => chat.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    checkIfScrolledToBottom();
  }, [messages]);

  return (
    <>
      <div style={{ display: "flex" }}>
        <KcgHeader scrollContainerRef={scrollRef} />
        <Sidebar onNewChat={startNewSession} />
        <div className="kcg-chat-window" ref={scrollRef}>
          <div className="chat-header">
            <a
              href="https://d36oiwf74r1rap.cloudfront.net/wp-content/uploads/2025/01/UI2025.pdf"
              target="_blank"
              className="text-[#eab308] hover:text-[#eab308]"
            >
              Utah Informed 2025 - A Report created by Kem C. Gardner Institute
            </a>
          </div>
          <div className="chat-history" ref={chatHistoryRef}>
            {messages.map((msg, idx) => {
              let displayIndex = null;
              if (msg.sender !== "user") {
                displayIndex = customIndex;
                customIndex += 1;
              }

              return (
                <div
                  key={msg.id || idx}
                  style={{
                    display: "flex",
                    justifyContent:
                      msg.sender === "user" ? "flex-end" : "flex-start",
                    alignItems: "center",
                  }}
                >
                  <div
                    className={`chat-bubble ${
                      msg.sender === "user" ? "user" : "bot"
                    }`}
                  >
                    {msg.sender === "user" ? (
                      msg.text
                    ) : msg.isTyping ? (
                      <div
                        style={{ display: "flex", alignItems: "flex-start" }}
                      >
                        <img
                          src={betsyLogo}
                          alt="Betsy"
                          className="betsylogosize"
                        />
                        <div style={{ flex: 1, color: "#ffffff" }}>
                          <Typewriter
                            key={`typing-loader-${idx}`}
                            words={["Typing..."]}
                            loop={1}
                            cursor
                            cursorStyle="|"
                            typeSpeed={10}
                            deleteSpeed={0}
                            delaySpeed={300}
                          />
                        </div>
                      </div>
                    ) : msg.text.startsWith("Hello there! I'm BETSY") ? (
                      <div
                        style={{ display: "flex", alignItems: "flex-start" }}
                      >
                        <img
                          src={betsyLogo}
                          alt="Betsy"
                          className="betsylogosize"
                        />
                        <div style={{ flex: 1 }}>
                          {msg.isAnimated ? (
                            <TypewriterMarkdown text={WelcomeMSg} />
                          ) : (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw]}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          )}
                        </div>
                      </div>
                    ) : regeneratingMsgId === msg.id ? (
                      <div
                        style={{ display: "flex", alignItems: "flex-start" }}
                      >
                        <img
                          src={betsyLogo}
                          alt="Betsy"
                          className="betsylogosize"
                        />
                        <div style={{ flex: 1, color: "#ffffff" }}>
                          <Typewriter
                            key={`typing-loader-${idx}`}
                            words={["Typing..."]}
                            loop={1}
                            cursor
                            cursorStyle="|"
                            typeSpeed={10}
                            deleteSpeed={0}
                            delaySpeed={300}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{ display: "flex", alignItems: "flex-start" }}
                        >
                          <img
                            src={betsyLogo}
                            alt="Betsy"
                            className="betsylogosize"
                          />
                          <div style={{ flex: 1 }}>
                            {msg.isAnimated ? (
                              <TypewriterMarkdown
                                key={`bot-msg-${msg.id || idx}`}
                                text={
                                  msg.text.trim() === "_"
                                    ? "As I am Bodex Betsy, I can only help you with Utah Informed 2025 - A Report created by Kem C Gardner Institute."
                                    : msg.text
                                }
                              />
                            ) : (
                              <ReactMarkdown>{msg.text}</ReactMarkdown>
                            )}
                          </div>
                        </div>
                        <div style={{ marginLeft: "70px" }}>
                          {messages.length > 2 && msg.time && (
                            <p style={{ color: "#fa9a25", fontSize: "12px" }}>
                              Response Time : {msg.time} secs
                            </p>
                          )}
                          <>
                            <IconButton
                              onClick={() =>
                                copyText(
                                  msg.text.trim() === "_"
                                    ? "As I am Bodex Betsy, I can only help you with the Utah Informed 2025 - A Report created by Kem C Gardner Institute."
                                    : msg.text,
                                )
                              }
                              size="small"
                              style={{ color: "#fff" }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => regenerateResponse(msg)}
                              size="small"
                              style={{ color: "#fff" }}
                            >
                              <AutorenewIcon fontSize="small" />
                            </IconButton>

                            <IconButton onClick={() => handleLike(msg.id)}>
                              {msg.feedback === "like" ? (
                                <ThumbUpAltIcon style={{ color: "#fff" }} />
                              ) : (
                                <ThumbUpOffAltIcon style={{ color: "#fff" }} />
                              )}
                            </IconButton>

                            <IconButton onClick={() => handleDislike(msg.id)}>
                              {msg.feedback === "dislike" ? (
                                <ThumbDownAltIcon style={{ color: "#fff" }} />
                              ) : (
                                <ThumbDownOffAltIcon
                                  style={{ color: "#fff" }}
                                />
                              )}
                            </IconButton>

                            <SpeechControls text={msg.text} />
                          </>
                        </div>
                      </>
                    )}
                  </div>
                  {msg.sender === "user" && (
                    <img
                      src={userLogo}
                      alt="User"
                      style={{
                        height: 60,
                        width: 60,
                        marginLeft: "0.5rem",
                        borderRadius: "50%",
                        marginTop: "-20px",
                      }}
                    />
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {showScrollDown && (
            <div
              onClick={scrollToBottom}
              className="scroll-down-btn"
              aria-label="Scroll to bottom"
            >
              <ArrowCircleDownIcon style={{ color: "#fff" }} fontSize="large" />
            </div>
          )}

          <ChatInput
            input={input}
            setInput={setInput}
            onSend={handleSend}
            isTyping={isTyping}
          />

          <Snackbar
            open={copySnackbarOpen}
            autoHideDuration={2000}
            onClose={() => setCopySnackbarOpen(false)}
            message={<span className="snackbarmsg">{snackBarMsg}</span>}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setCopySnackbarOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          />
        </div>
      </div>
      <BetsyFooter />
      <KcgQuestion setInput={setInput} />
    </>
  );
};

export default KCG;
